import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { UserInfo } from '@hobt/user-domain';

export interface AppConfigCache {
  moduleMap: Record<string, number[]>;
  globalMenuAccesses: Record<string, boolean>;
  user: UserInfo | null;
}

export function useCacheApplicationConfig(
  moduleMap?: Record<string, number[]>,
  globalMenuAccesses?: Record<string, boolean>,
  user?: UserInfo
): AppConfigCache {
  const queryClient = useQueryClient();

  const MODULE_MAP_KEY = 'moduleMap';
  const GLOBAL_MENU_KEY = 'globalMenuAccesses';
  const USER_KEY = 'user';

  useEffect(() => {
    if (moduleMap) {
      queryClient.setQueryData(['moduleMap'], moduleMap);
      localStorage.setItem(MODULE_MAP_KEY, JSON.stringify(moduleMap));
    }

    if (globalMenuAccesses) {
      const sanitizedAccesses: Record<string, boolean> = Object.fromEntries(
        Object.entries(globalMenuAccesses).map(([k, v]) => [k, !!v])
      );
      queryClient.setQueryData(['globalMenuAccesses'], sanitizedAccesses);
      localStorage.setItem(GLOBAL_MENU_KEY, JSON.stringify(sanitizedAccesses));
    }

    if (user) {
      queryClient.setQueryData(['user'], user);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
  }, [moduleMap, globalMenuAccesses, user, queryClient]);

  const cachedModuleMap: Record<string, number[]> = (() => {
    const fromQuery = queryClient.getQueryData<Record<string, number[]>>(['moduleMap']);
    if (fromQuery) return fromQuery;
    try {
      return JSON.parse(localStorage.getItem(MODULE_MAP_KEY) || '{}');
    } catch {
      return {};
    }
  })();

  const cachedGlobalMenuAccesses: Record<string, boolean> = (() => {
    const fromQuery = queryClient.getQueryData<Record<string, boolean>>(['globalMenuAccesses']);
    if (fromQuery) return fromQuery;
    try {
      const parsed = JSON.parse(localStorage.getItem(GLOBAL_MENU_KEY) || '{}');
      return Object.fromEntries(Object.entries(parsed).map(([k, v]) => [k, !!v]));
    } catch {
      return {};
    }
  })();

  const cachedUser: UserInfo | null = (() => {
    const fromQuery = queryClient.getQueryData<UserInfo>(['user']);
    if (fromQuery) return fromQuery;
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    } catch {
      return null;
    }
  })();

  return {
    moduleMap: cachedModuleMap,
    globalMenuAccesses: cachedGlobalMenuAccesses,
    user: cachedUser
  };
}
